"use client";

import { useSailingCategory } from "./SailingCategoryControl";
import { useWindUnit } from "./WindUnitControl";
import { formatWind } from "@/lib/conditions/forecast-presentation";
import type { FactorConfidence } from "@/lib/conditions/factor-explanations";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import { scoreBadgeColor } from "@/lib/conditions/scoring";
import {
  weatherCodeEmoji,
  weatherCodeLabel,
} from "@/lib/conditions/weather-code-display";

type Point = {
  hour: number;
  clubScore: number | null;
  performanceScore: number | null;
  windKn: number | null;
  gustKn: number | null;
  dirDeg: number | null;
  tempC: number | null;
  precipProbabilityPct: number | null;
  weatherCode: number | null;
  isDay: boolean;
  clubConfidence: FactorConfidence;
  performanceConfidence: FactorConfidence;
  clubSafetyState: "clear" | "partial" | "amber" | "red" | "unavailable";
  performanceSafetyState: "clear" | "partial" | "amber" | "red" | "unavailable";
};

function degToCompass(deg: number): string {
  const dirs = [
    "N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE",
    "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW",
  ];
  return dirs[Math.round((deg % 360) / 22.5) % 16];
}

function ScoreCell({
  label,
  score,
  confidence,
  safetyState,
}: {
  label: string;
  score: number | null;
  confidence: FactorConfidence;
  safetyState: Point["clubSafetyState"];
}) {
  const evidence = (
    <div className="mt-1 space-y-1">
      <p className={`rounded-full px-2 py-1 text-[9px] font-semibold ${safetyClass(safetyState)}`}>
        {safetyLabel(safetyState)}
      </p>
      <p className="px-1 text-[9px] font-medium text-text-muted">
        {confidenceLabel(confidence)}
      </p>
    </div>
  );

  if (score === null) {
    return (
      <div>
        <div className="rounded-[8px] bg-slate-100 px-2 py-1.5 text-slate-600">
          <div className="text-[9px] font-semibold uppercase tracking-[0.5px]">{label}</div>
          <div className="mt-0.5 text-[11px] font-semibold">Unavailable</div>
        </div>
        {evidence}
      </div>
    );
  }

  const badge = scoreBadgeColor(score);
  return (
    <div>
      <div className={`rounded-[8px] px-2 py-1.5 ${badge.bg} ${badge.text}`}>
        <div className="text-[9px] font-semibold uppercase tracking-[0.5px] opacity-80">{label}</div>
        <div className="mt-0.5 font-serif text-[17px] font-bold leading-none tabular-nums">
          {scoreOutOf100(score)}
        </div>
      </div>
      {evidence}
    </div>
  );
}

function confidenceLabel(confidence: FactorConfidence): string {
  if (confidence === "unavailable") return "Confidence unavailable";
  if (confidence === "partial") return "Partial confidence";
  if (confidence === "inferred") return "Inferred confidence";
  return "Full confidence";
}

function safetyLabel(state: Point["clubSafetyState"]): string {
  if (state === "red") return "Red safety flag";
  if (state === "amber") return "Caution";
  if (state === "partial") return "Safety evidence partial";
  if (state === "unavailable") return "Safety evidence unavailable";
  return "No safety flags";
}

function safetyClass(state: Point["clubSafetyState"]): string {
  if (state === "red") return "bg-red-50 text-red-700";
  if (state === "amber") return "bg-amber-50 text-amber-700";
  if (state === "partial") return "bg-amber-50 text-amber-700";
  if (state === "unavailable") return "bg-slate-100 text-slate-600";
  return "bg-emerald-50 text-emerald-700";
}

function hourlyDateLabel(
  forecastDate: string,
  isSpotLocalToday: boolean,
): string {
  if (isSpotLocalToday) return "Today, hour by hour";
  const parsed = new Date(`${forecastDate}T12:00:00.000Z`);
  if (!Number.isFinite(parsed.getTime())) return "Selected forecast, hour by hour";
  const date = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(parsed);
  return `${date}, hour by hour`;
}

export function SailingWindTimeline({
  hourly,
  forecastDate,
  isSpotLocalToday,
}: {
  hourly: Point[];
  forecastDate: string;
  isSpotLocalToday: boolean;
}) {
  const category = useSailingCategory();
  const unit = useWindUnit("kn");
  if (hourly.length === 0) return null;

  return (
    <div className="rounded-[18px] border border-border-light bg-white p-5 md:p-7">
      <div className="mb-4 md:flex md:items-end md:justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[1.2px] text-gold">
            {hourlyDateLabel(forecastDate, isSpotLocalToday)}
          </p>
          <h2 className="mt-1 font-serif text-[20px] font-bold text-navy md:text-[24px]">
            Sailing scores and weather
          </h2>
        </div>
        <div className="mt-2 flex flex-wrap gap-3 text-[11px] font-semibold text-text-muted md:mt-0">
          {category !== "hp" && <span><span className="text-navy">Club Dinghy</span> score</span>}
          {category !== "club" && <span><span className="text-teal-dark">High Performance</span> score</span>}
        </div>
      </div>

      <div
        role="list"
        aria-label={`Hourly ${category === "compare" ? "Club Dinghy and High Performance" : category === "club" ? "Club Dinghy" : "High Performance"} scores with weather`}
        className="flex snap-x snap-mandatory gap-2.5 overflow-x-auto pb-3"
      >
        {hourly.map((point) => {
          const weatherLabel = point.weatherCode === null
            ? "Weather unavailable"
            : weatherCodeLabel(point.weatherCode);
          return (
            <article
              key={point.hour}
              role="listitem"
              className={`w-[142px] flex-none snap-start rounded-[12px] border p-2.5 ${
                point.isDay
                  ? "border-border-light bg-white"
                  : "border-slate-200 bg-slate-50"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <time className="text-[11px] font-bold text-navy" dateTime={`${String(point.hour).padStart(2, "0")}:00`}>
                  {String(point.hour).padStart(2, "0")}:00
                </time>
                <span
                  className="text-[20px] leading-none"
                  aria-label={weatherLabel}
                  title={weatherLabel}
                >
                  {point.weatherCode === null ? "?" : weatherCodeEmoji(point.weatherCode)}
                </span>
              </div>

              <div className="mt-2 grid gap-1.5">
                {category !== "hp" && <ScoreCell
                  label="Club Dinghy"
                  score={point.clubScore}
                  confidence={point.clubConfidence}
                  safetyState={point.clubSafetyState}
                />}
                {category !== "club" && <ScoreCell
                  label="High Performance"
                  score={point.performanceScore}
                  confidence={point.performanceConfidence}
                  safetyState={point.performanceSafetyState}
                />}
              </div>

              <div className="mt-2 space-y-0.5 text-[10px] leading-[1.45] text-text-muted">
                <p>
                  {point.windKn === null
                    ? "Wind unavailable"
                    : `${formatWind(point.windKn * 1.852, unit)}${point.dirDeg === null ? "" : ` ${degToCompass(point.dirDeg)}`}`}
                </p>
                <p>{point.gustKn === null ? "Gusts unavailable" : `${formatWind(point.gustKn * 1.852, unit)} gusts`}</p>
                <p>{point.tempC === null ? "Temperature unavailable" : `${Math.round(point.tempC)}°C`}</p>
                <p>
                  {point.precipProbabilityPct === null
                    ? "Rain chance unavailable"
                    : `${Math.round(point.precipProbabilityPct)}% rain chance`}
                </p>
              </div>

            </article>
          );
        })}
      </div>

      <p className="mt-2 text-[11px] leading-relaxed text-text-muted">
        All times are local to the venue. Use the boat selector above to switch category or compare both.
      </p>
    </div>
  );
}
